import { BossDef, BossId, getBossDef } from "./bosses";

// Re-export BossId for convenience
export type { BossId } from "./bosses";
import { EnemyInstance, createEnemyInstance } from "./enemies";
import { EnemyFactory } from "./enemyFactory";
import {
  applyEnemyScaling,
  EnemyScalingMultipliers,
  getEnemyScalingMultipliers,
} from "../state/enemyScaling";
import { applyPerks } from "./perks";
import { PrimitiveBlueprint } from "../avatars/types";

/**
 * Boss instance type - same as EnemyInstance but created from BossDef.
 * Bosses are enhanced enemies with higher stats and special perks.
 */
export type BossInstance = EnemyInstance;

export type BossFactoryOptions = {
  floor?: number;
  scalingMultipliers?: EnemyScalingMultipliers;
};

/**
 * Centralized boss factory for creation, scaling, and blueprint lookup.
 * Bosses follow the same pattern as enemies but with enhanced stats and unique perks.
 */
export class BossFactory {
  /**
   * Create a boss instance from its ID with optional scaling.
   * Bosses are scaled more aggressively than regular enemies (1.5x multiplier).
   *
   * @param bossId - The ID of the boss type to create
   * @param options - Optional configuration for scaling
   * @returns A fully configured BossInstance (same as EnemyInstance)
   */
  static create(bossId: BossId, options: BossFactoryOptions = {}): BossInstance {
    const bossDef = getBossDef(bossId);

    // Create base enemy instance using the boss's base enemy type
    // This gives us the correct visual blueprint and base structure
    const baseEnemyDef = EnemyFactory.getDef(bossDef.baseEnemyId);

    // Create instance with boss stats (overriding base enemy stats)
    // Use base enemy ID for the instance ID (for blueprint lookup)
    // Boss name distinguishes it as a boss
    const instance = createEnemyInstance(
      {
        ...baseEnemyDef,
        id: bossDef.baseEnemyId, // Use base enemy ID for blueprint/type matching
        name: bossDef.name, // Boss name distinguishes it visually
        maxHp: bossDef.maxHp,
        atk: bossDef.atk,
        armor: bossDef.armor,
        defendChance: bossDef.defendChance ?? 0,
        perks: bossDef.perks ? [...bossDef.perks] : [],
      },
      undefined // Bosses don't use name suffixes
    );

    // Apply boss scaling (more aggressive than regular enemies)
    // Bosses get 1.5x scaling multiplier compared to regular enemies
    if (options.floor !== undefined) {
      const baseMultipliers = getEnemyScalingMultipliers(options.floor);
      const bossMultipliers: EnemyScalingMultipliers = {
        hpMultiplier: baseMultipliers.hpMultiplier * 1.5,
        atkMultiplier: baseMultipliers.atkMultiplier * 1.5,
        armorMultiplier: baseMultipliers.armorMultiplier * 1.2, // Slightly less armor scaling
      };
      applyEnemyScaling(instance, bossMultipliers);
    } else if (options.scalingMultipliers) {
      // Apply custom multipliers with boss boost
      const bossMultipliers: EnemyScalingMultipliers = {
        hpMultiplier: options.scalingMultipliers.hpMultiplier * 1.5,
        atkMultiplier: options.scalingMultipliers.atkMultiplier * 1.5,
        armorMultiplier: options.scalingMultipliers.armorMultiplier * 1.2,
      };
      applyEnemyScaling(instance, bossMultipliers);
    }

    // Apply boss perks after scaling (base → scale → perks sequence)
    if (instance.perks.length > 0) {
      applyPerks(instance, instance.perks);
    }

    return instance;
  }

  /**
   * Get the visual blueprint for rendering a boss.
   * Bosses use the blueprint of their base enemy type.
   *
   * @param bossId - The ID of the boss type
   * @returns The PrimitiveBlueprint for rendering
   */
  static getBlueprint(bossId: BossId): PrimitiveBlueprint {
    const bossDef = getBossDef(bossId);
    return EnemyFactory.getBlueprint(bossDef.baseEnemyId);
  }

  /**
   * Get the definition for a boss type.
   * @param bossId - The ID of the boss type
   * @returns The BossDef definition
   */
  static getDef(bossId: BossId): BossDef {
    return getBossDef(bossId);
  }
}
